import {
  ConsoleInstrumentation,
  CSPInstrumentation,
  ErrorsInstrumentation,
  initializeFaro,
  LogLevel,
  PerformanceInstrumentation,
  SessionInstrumentation,
  UserActionInstrumentation,
  WebVitalsInstrumentation,
  type Faro,
} from "@grafana/faro-web-sdk";
import { createSanitizer, type ScrubRule } from "./beforeSend";
import { redactUrl, scrub } from "./scrub";
import type { CoreTelemetryEvents, TelemetryAttributeValue } from "./telemetryEvents";

export type TelemetryAttributes = Record<string, TelemetryAttributeValue>;

export interface TelemetryConfig {
  /** Faro collector URL. Empty or missing disables telemetry. */
  url?: string;
  /** Faro app name, e.g. `froppio-forms`. */
  appName: string;
  version: string;
  environment: string;
  /** API base URL; browser trace context is propagated only to this origin. */
  apiBaseUrl: string;
  /** Maps a pathname to the route template used for page URLs; defaults to `redactUrl`. */
  routeTemplate?: (pathname: string) => string;
  /** App-specific secrets to mask in all free text. */
  extraScrub?: readonly ScrubRule[];
  /** Extra non-personal attributes for the `app.started` event. */
  startedAttributes?: TelemetryAttributes;
}

let faro: Faro | undefined;
let currentRoute = "/";
let user: { id?: string; attributes: Record<string, string | undefined> } = { attributes: {} };
const errorIds = new WeakMap<object, string>();

const toAttributes = (attributes: object) => Object.fromEntries(
  Object.entries(attributes).flatMap(([key, value]) => (value === undefined ? [] : [[key, String(value)]])),
);

// Telemetry must never break the app, so every Faro call is guarded.
function withFaro(callback: (instance: Faro) => void) {
  if (!faro) return;
  try { callback(faro); } catch { /* ignore telemetry failures */ }
}

/** Starts Faro when a collector URL is configured. Call once, before rendering, so startup crashes are captured. */
export function initTelemetry(config: TelemetryConfig) {
  const url = config.url?.trim();
  if (faro || !url || typeof window === "undefined") return;
  try {
    faro = initializeFaro({
      url,
      app: { name: config.appName, version: config.version, environment: config.environment },
      instrumentations: [
        new ErrorsInstrumentation(),
        new WebVitalsInstrumentation(),
        new SessionInstrumentation(),
        new PerformanceInstrumentation(),
        new ConsoleInstrumentation(),
        new CSPInstrumentation(),
        new UserActionInstrumentation(),
      ],
      sessionTracking: { enabled: true, persistent: true },
      consoleInstrumentation: {
        disabledLevels: [LogLevel.TRACE, LogLevel.DEBUG, LogLevel.LOG, LogLevel.INFO],
        // Errors are reported by boundaries and global handlers; console.error stays a log to avoid duplicates.
        consoleErrorAsLog: true,
      },
      ignoreErrors: [/ResizeObserver loop/],
      beforeSend: createSanitizer({ routeTemplate: config.routeTemplate, extraScrub: config.extraScrub }),
    });
  } catch (error) {
    faro = undefined;
    console.warn("Telemetry could not start.", error);
    return;
  }

  void loadTracing(config.apiBaseUrl);
  const onConnectivity = () => trackEvent("app.connectivity_changed", { online: navigator.onLine });
  window.addEventListener("online", onConnectivity);
  window.addEventListener("offline", onConnectivity);
  trackEvent("app.started", {
    ...config.startedAttributes,
    online: navigator.onLine,
    standalone: window.matchMedia?.("(display-mode: standalone)").matches ?? false,
  });
}

// Browser spans for fetch calls; trace context is only propagated to our API so the backend joins the trace.
async function loadTracing(apiBaseUrl: string) {
  try {
    const { TracingInstrumentation } = await import("@grafana/faro-web-tracing");
    const apiOrigin = new URL(apiBaseUrl, window.location.href).origin;
    const escaped = apiOrigin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    withFaro(instance => instance.instrumentations.add(new TracingInstrumentation({
      instrumentationOptions: { propagateTraceHeaderCorsUrls: [new RegExp(`^${escaped}/`)] },
    })));
  } catch (error) {
    console.warn("Telemetry tracing could not start.", error);
  }
}

export const isTelemetryEnabled = () => !!faro;

/** Untyped event sink; apps expose a typed wrapper over their own event catalogue. */
export function trackEvent<K extends keyof CoreTelemetryEvents>(name: K, attributes: CoreTelemetryEvents[K]): void;
export function trackEvent(name: string, attributes: object): void;
export function trackEvent(name: string, attributes: object) {
  withFaro(instance => instance.api.pushEvent(name, toAttributes(attributes)));
}

/** Reports a failed API request with the endpoint as a template (no query string or IDs). */
export function trackApiFailure(details: Omit<CoreTelemetryEvents["api.request_failed"], "endpoint"> & { path: string }) {
  const { path, ...rest } = details;
  trackEvent("api.request_failed", { ...rest, endpoint: redactUrl(path) });
}

/**
 * Starts a Faro user action unless one is already active (e.g. started by a
 * `data-faro-user-action-name` control), in which case the work joins that action.
 */
export function startUserAction(name: string, options: { trigger: string; critical?: boolean }) {
  withFaro(instance => {
    if (instance.api.getActiveUserAction()) return;
    instance.api.startUserAction(name, { route: currentRoute }, {
      triggerName: options.trigger,
      importance: options.critical ? "critical" : "normal",
    });
  });
}

/** Short identifier for one error occurrence, stable for the same error object. */
export function getErrorId(error: unknown): string {
  const create = () => Math.random().toString(36).slice(2, 8);
  if (typeof error !== "object" || error === null) return create();
  let id = errorIds.get(error);
  if (!id) errorIds.set(error, id = create());
  return id;
}

export const getTelemetrySessionId = (): string | undefined => {
  try { return faro?.api.getSession()?.id; } catch { return undefined; }
};

/** Code users can share with support to locate an error in Grafana. */
export function getSupportId(error: unknown) {
  const sessionId = getTelemetrySessionId();
  const errorId = getErrorId(error);
  return sessionId ? `${sessionId}-${errorId}` : errorId;
}

export interface ReportErrorOptions {
  source: string;
  fatal?: boolean;
  context?: TelemetryAttributes;
}

export function reportError(error: unknown, { source, fatal, context = {} }: ReportErrorOptions) {
  const errorId = getErrorId(error);
  withFaro(instance => {
    const value = error instanceof Error
      ? error
      : new Error(typeof error === "string" ? scrub(error) : "A non-error value was thrown.");
    instance.api.pushError(value, {
      fatal,
      context: toAttributes({ ...context, source, errorId, route: currentRoute }),
    });
  });
  return errorId;
}

export const getTelemetryRoute = () => currentRoute;

/** Sets the current route template and, optionally, scope attributes such as `businessId`. */
export function setTelemetryRoute(route: string, scope?: Record<string, string | undefined>) {
  currentRoute = route;
  withFaro(instance => instance.api.setView({ name: route }));
  if (scope) setTelemetryAttributes(scope);
}

/** Merges non-personal identifiers (e.g. `businessId`) into the user attributes sent with every signal. */
export function setTelemetryAttributes(attributes: Record<string, string | undefined>) {
  const next = { ...user.attributes, ...attributes };
  if (Object.keys(next).every(key => next[key] === user.attributes[key])) return;
  applyUser({ ...user, attributes: next });
}

/** Sets the signed-in user ID; `null` clears the user and their scope attributes. */
export function setTelemetryUser(id: string | null) {
  if ((id ?? undefined) === user.id) return;
  applyUser(id ? { ...user, id } : { attributes: {} });
}

function applyUser(next: typeof user) {
  user = next;
  withFaro(instance => {
    const attributes = toAttributes(user.attributes);
    if (!user.id && Object.keys(attributes).length === 0) {
      instance.api.resetUser();
      return;
    }
    instance.api.setUser({ id: user.id, attributes });
  });
}
